// consulta/src/components/DoctorScheduleFullPage.js
import React, { useState, useEffect } from 'react';
import { ChevronLeft, Calendar, SearchX } from 'lucide-react';
import DoctorIdentityCard from './DoctorIdentityCard';
import { DoctorScheduleInline } from './DoctorDirectoryPage';
import { doctorService } from '../services/doctorService';
import specialtyService from '../services/specialtyService';
import GlobalLoader from './GlobalLoader';

/**
 * Pantalla full-screen con el schedule completo de una doctora.
 * Llega desde MatchConfirmationPage (back button) o como destino
 * directo si la usuaria quiere ver más opciones de su match.
 *
 * URL: /horarios-doctora?id=<doctorId>&specialty=<name>
 *
 * Props:
 *  - onSlotClick(specialtyId, slot) — callback al elegir un slot.
 *      Usa el patrón existente de App.js handleRouteStartBooking,
 *      pero acá lo overrideamos para navegar a /agendar-match en lugar
 *      de iniciar el flow directo.
 *  - onBackToDirectory() — callback al tap "Ver otras doctoras".
 */
const DoctorScheduleFullPage = ({ onSlotClick, onBackToDirectory }) => {
  const [doctor, setDoctor] = useState(null);
  const [specialty, setSpecialty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      const params = new URLSearchParams(window.location.search);
      const doctorId = parseInt(params.get('id'), 10);
      const specialtyName = params.get('specialty');

      if (!doctorId) {
        setError('missing-id');
        setLoading(false);
        return;
      }
      try {
        // Resolver specialty desde el nombre o desde la doctora.
        const allSpecs = await specialtyService.getFormattedOnDemandSpecialties();
        let spec = null;
        if (specialtyName) {
          const norm = specialtyName.trim().toLowerCase();
          spec = allSpecs.find(s => (s.name || '').trim().toLowerCase() === norm);
        }

        // Buscar la doctora
        let dr = null;
        if (spec) {
          const doctors = await doctorService.getDoctorsBySpecialty(spec.id);
          dr = doctors.find(d => d.id === doctorId);
        }
        if (!dr) {
          // Fallback: buscar en TODAS las specialties
          for (const s of allSpecs) {
            const doctors = await doctorService.getDoctorsBySpecialty(s.id);
            const found = doctors.find(d => d.id === doctorId);
            if (found) {
              dr = found;
              spec = spec || s;
              break;
            }
          }
        }

        if (!dr) {
          setError('doctor-not-found');
          setLoading(false);
          return;
        }

        // Cargar config completo de la specialty (durations)
        const configMap = await specialtyService.getSpecialtyConfig(spec.id);
        const fullSpec = configMap?.[spec.id] || spec;

        setDoctor(dr);
        setSpecialty(fullSpec);
      } catch (err) {
        console.warn('[DoctorScheduleFullPage] load error:', err);
        setError('load-failed');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  if (loading) {
    return <GlobalLoader title="Cargando horarios" subtitle="Buscando los horarios de tu doctora" />;
  }

  if (error || !doctor) {
    return (
      <div className="min-h-screen bg-warm-canvas flex items-center justify-center p-6">
        <div className="bg-surface-card rounded-card p-8 max-w-md text-center">
          <SearchX className="mx-auto mb-4 text-brand/50" size={44} aria-hidden="true" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            No encontramos esta doctora
          </h3>
          <p className="text-text-secondary text-sm mb-6">
            Pero hay muchas otras esperándote.
          </p>
          <button
            onClick={onBackToDirectory}
            className="px-6 py-3 bg-daisies text-white rounded-card font-bold hover:bg-daisies/90 transition"
          >
            Ver doctoras disponibles
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-canvas">
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-brand-100 px-4 py-3 flex items-center gap-3">
        <button
          onClick={handleBack}
          className="p-2 -ml-2 rounded-full hover:bg-surface transition"
          aria-label="Volver"
        >
          <ChevronLeft size={22} className="text-text-primary" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-text-secondary">Volver al match</p>
          <p className="text-sm font-bold text-text-primary truncate">
            {doctor.name} {doctor.last_name}
          </p>
        </div>
      </header>

      <main className="px-4 py-5 max-w-2xl mx-auto">
        <div className="bg-white rounded-card p-5 mb-5">
          <DoctorIdentityCard doctor={doctor} size="standard" />
        </div>

        <div className="bg-white rounded-card p-5">
          <p className="text-xs font-semibold text-text-secondary mb-3 flex items-center gap-1.5">
            <Calendar size={13} className="text-daisies" />
            Horarios disponibles
          </p>
          <DoctorScheduleInline
            doctorId={doctor.id}
            specialty={specialty}
            onSlotClick={(slot) => {
              const durationId = specialty?.allDurations?.[0]?.id;
              if (!durationId && process.env.NODE_ENV !== 'production') {
                console.warn('[DoctorScheduleFullPage] duration_id undefined; booking flow may downgrade');
              }
              onSlotClick(specialty.id, {
                ...slot,
                doctor: doctor,
                professional_ids: [{ id: doctor.id }],
                professional_id: doctor.id,
                duration_id: durationId,
              });
            }}
          />
        </div>

        <div className="text-center mt-6">
          <button
            onClick={onBackToDirectory}
            className="text-sm text-text-secondary hover:text-daisies transition"
          >
            Ver otras doctoras de {specialty?.name || 'esta especialidad'} →
          </button>
        </div>
      </main>
    </div>
  );
};

export default DoctorScheduleFullPage;
